// src/pages/HostProfileSetupPage.js
import React, { useState, useMemo } from 'react';
import {
  Container, Typography, TextField, Button, Alert, FormGroup,
  FormControlLabel, Checkbox, MenuItem, Select, InputLabel, FormControl, Box
} from '@mui/material';
import { http } from "../lib/axios";
import { useNavigate } from 'react-router-dom';
import {
  LIGHTING_ENVIRONMENT_OPTIONS,
  ART_EVENT_OPTIONS,
  ART_TYPE_OPTIONS,
  VENUE_TYPE_OPTIONS,
  FOOT_TRAFFIC_OPTIONS,
  DISPLAY_AVAILABILITY_OPTIONS
} from '../utils/hostProfileOptions';
import { geocodeAddress } from '../lib/geocode';
import AgreementAcceptModal from "../components/AgreementAcceptModal";
import { getAgreementStatus } from "../lib/apiAgreements";
import ContextHelpButton from '../components/ContextHelpButton';

export default function HostProfileSetupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    address: '',
    venue_type: '',
    venue_description: '',
    display_space_details: '',
    lighting_environment_control: [],
    past_art_events: [],
    art_preferences: [],
    curation_philosophy: '',
    staffing_during_open_hours: false,
    foot_traffic_estimate: '',
    artwork_display_availability: '',
    hosting_motivation: '',
    hosting_outcome_goals: ''
  });
  const [error, setError] = useState(null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const agreementConfig = useMemo(() => {
    const isGalleryHost = form.venue_type === 'art_gallery';
    if (isGalleryHost) {
      return {
        agreementCode: "GALLERY_MEMBERSHIP",
        title: "Gallery Host Membership Agreement",
        workflowContext: { workflow: "gallery_host_onboarding", promo_code: "EARLY_GALLERY_HOST6_12PM_PLATFORM" }
      };
    }
    return {
      agreementCode: "HOST_MEMBERSHIP",
      title: "ARTflow Host Membership Agreement",
      workflowContext: { workflow: "artflow_host_onboarding", promo_code: "EARLY_ARTFLOW_HOST6_6PM_PLATFORM" }
    };
  }, [form.venue_type]);

  const hasAcceptedHostAgreement = async (agreementCode) => {
    try {
      const res = await getAgreementStatus([agreementCode]);
      const row = res.results?.find(item => item.code === agreementCode);
      return !!row?.accepted;
    } catch (error) {
      console.error('Failed to check host agreement status:', error);
      return false;
    }
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm({ ...form, [name]: checked });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleMultiSelectChange = (e, name) => {
    setForm({ ...form, [name]: e.target.value });
  };

  const getCoordinatesFromAddress = async (address) => {
    const data = await geocodeAddress(address);
    if (data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry;
      return { latitude: lat, longitude: lng };
    }
    throw new Error('Address not found');
  };

  const getCurrentUserId = async () => {
    const { data } = await http.get('/api/accounts/me/');
    return data.id;
  };

  const fetchExistingHost = async (userId) => {
    const { data } = await http.get(`/hosts/?user=${userId}`);
    return data.length > 0 ? data[0] : null;
  };

  const handleFinalSubmit = async () => {
    try {
      // 1) who is the user?
      const userId = await getCurrentUserId();

      // 2) prevent duplicate profile
      const existingHost = await fetchExistingHost(userId);
      if (existingHost) {
        alert('You already have a Host profile. Redirecting you to your dashboard.');
        navigate('/dashboard/host');
        return;
      }

      // 3) geocode
      const coords = await getCoordinatesFromAddress(form.address);

      // 4) create including required user field
      await http.post('/hosts/', {
        user: { id: userId },
        ...form,
        location: coords
      });

      navigate('/dashboard/host');
    } catch (err) {
      console.error('Failed to create host profile:', err?.response?.data || err?.message);
      setError('Failed to create host profile.');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const accepted = await hasAcceptedHostAgreement(agreementConfig.agreementCode);
    if (!accepted) {
      setPendingSubmit(true);
      setShowAgreementModal(true);
      return;
    }
    await handleFinalSubmit();
  };

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
          Set Up Your Host Profile
        </Typography>
        <ContextHelpButton section="host-help" title="Help for host profile setup" />
      </Box>
      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

      {/* White background form card */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: 2,
          p: 3,
          bgcolor: '#ffffff',
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <TextField
          fullWidth
          label="Address"
          name="address"
          onChange={handleChange}
          margin="normal"
          required
        />

        {/* Venue Type as dropdown */}
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="venue-type-label">Venue Type</InputLabel>
          <Select
            labelId="venue-type-label"
            name="venue_type"
            value={form.venue_type}
            label="Venue Type"
            onChange={handleChange}
          >
            {VENUE_TYPE_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Venue Description"
          name="venue_description"
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Display Space Details"
          name="display_space_details"
          onChange={handleChange}
          margin="normal"
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Lighting & Environment Control</InputLabel>
          <Select
            multiple
            value={form.lighting_environment_control}
            onChange={e => handleMultiSelectChange(e, 'lighting_environment_control')}
          >
            {LIGHTING_ENVIRONMENT_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Past Art Events</InputLabel>
          <Select
            multiple
            value={form.past_art_events}
            onChange={e => handleMultiSelectChange(e, 'past_art_events')}
          >
            {ART_EVENT_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Art Preferences</InputLabel>
          <Select
            multiple
            value={form.art_preferences}
            onChange={e => handleMultiSelectChange(e, 'art_preferences')}
          >
            {ART_TYPE_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Curation Philosophy"
          name="curation_philosophy"
          onChange={handleChange}
          margin="normal"
          multiline
          rows={2}
        />

        <FormGroup sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="staffing_during_open_hours"
                checked={form.staffing_during_open_hours}
                onChange={handleChange}
              />
            }
            label="Staffing During Open Hours"
          />
        </FormGroup>

        <FormControl fullWidth margin="normal">
          <InputLabel>Foot Traffic Estimate</InputLabel>
          <Select
            name="foot_traffic_estimate"
            value={form.foot_traffic_estimate}
            onChange={handleChange}
          >
            {FOOT_TRAFFIC_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Availability for Artwork Display</InputLabel>
          <Select
            name="artwork_display_availability"
            value={form.artwork_display_availability}
            onChange={handleChange}
          >
            {DISPLAY_AVAILABILITY_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Why do you want to host art?"
          name="hosting_motivation"
          onChange={handleChange}
          margin="normal"
          multiline
          rows={2}
        />
        <TextField
          fullWidth
          label="What outcomes do you hope for?"
          name="hosting_outcome_goals"
          onChange={handleChange}
          margin="normal"
          multiline
          rows={2}
        />

        <Button type="submit" variant="contained" sx={{ mt: 2 }}>
          Save Profile
        </Button>
      </Box>
      <AgreementAcceptModal
        open={showAgreementModal}
        onClose={() => {
          setShowAgreementModal(false);
          setPendingSubmit(false);
        }}
        agreementCode={agreementConfig.agreementCode}
        title={agreementConfig.title}
        workflowContext={agreementConfig.workflowContext}
        onAccepted={async () => {
          setShowAgreementModal(false);
          if (pendingSubmit) {
            setPendingSubmit(false);
            await handleFinalSubmit();
          }
        }}
      />
    </Container>
  );
}
